import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { readItsTokenAndDT } from '../sso/its-sign-in';

/**
 * /authenticate: the NJ Jamaat sign-in page, for every njjamaat.org app. It lives only
 * on the main website because ITS only ever sends people back here.
 *
 *   already signed in        -> /callback (it returns the user to where they started)
 *   back from ITS (Token+DT) -> auth worker signs them in -> the starting app's /callback
 *   otherwise                -> show the Login button (goes to ITS OneLogin)
 */
export default function Authenticate() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, verifyToken, isAuthenticated } = useAuth();
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    // Token already being verified below: don't also redirect when that sign-in
    // flips isAuthenticated to true (the result's redirectUrl decides where to go).
    if (hasProcessedRef.current) return;

    if (isAuthenticated) {
      navigate('/callback', { replace: true });
      return;
    }

    // Token + DT are in the URL ITS redirected back to (both encrypted; only the auth
    // worker can check them). Without them, just show the Login button.
    const itsTokenAndDT = readItsTokenAndDT(location.search);
    if (!itsTokenAndDT) return;

    hasProcessedRef.current = true;
    verifyToken(itsTokenAndDT.token, itsTokenAndDT.dt).then((result) => {
      // redirectUrl is the starting app's /callback (from allowed-origins.js), possibly
      // another site, so a full page load. On failure, stay here so the user can retry.
      if (result?.success) window.location.assign(result.redirectUrl);
    });
  }, [isAuthenticated, location.search, navigate, verifyToken]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md">
        <h1 className="text-3xl font-bold mb-6 text-center text-gray-800">ITS Authentication Required</h1>
        <button
          onClick={login}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg transition duration-200"
        >
          Login
        </button>
      </div>
    </div>
  );
}
